import { useRef, useState } from 'react'
import { ImageIcon, Code2, Globe } from 'lucide-react'
import { sanitizeRichText } from '@/lib/sanitize'
import { useEditorStore } from '@/store/editorStore'
import { EditableText } from './EditableText'
import type { Block } from '@/domain/model/Block'
import type {
  HeroProps, TextProps, ImageProps, CtaProps, FeaturesProps, SpacerProps,
  TestimonialsProps, PricingProps, LogosProps, StatsProps, QuoteProps,
  CardsProps, ProductCatalogProps, GalleryProps, VideoProps, ColumnsProps, GridProps,
  ContactFormProps, EmbedProps, HtmlProps, BlockProps,
} from '@brix/types'

interface BlockPreviewProps {
  block: Block
  editable?: boolean
}

interface EditableProps {
  editable?: boolean
  onCommit?: (patch: Partial<BlockProps>) => void
}

function HeroPreview({ props, editable, onCommit }: { props: HeroProps } & EditableProps) {
  return (
    <div
      className="relative flex items-center justify-center text-white"
      style={{
        backgroundImage: props.backgroundImage ? `url(${props.backgroundImage})` : undefined,
        backgroundColor: props.backgroundImage ? undefined : '#1e1b4b',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: props.layout === 'full-height' ? '60vh' : '40vh',
        textAlign: props.alignment,
      }}
    >
      {props.backgroundImage && (
        <div className="absolute inset-0 bg-black/40" style={{ opacity: (props.overlayOpacity ?? 40) / 100 }} />
      )}
      <div className="relative z-10 max-w-3xl px-8 py-16">
        <h1 className="text-4xl font-bold leading-tight">
          <EditableText
            value={props.headline}
            editable={!!editable}
            onCommit={v => onCommit?.({ headline: v })}
            placeholder="Headline…"
          />
        </h1>
        {(props.subheadline || editable) && (
          <p className="mt-4 text-lg opacity-90">
            <EditableText
              value={props.subheadline ?? ''}
              editable={!!editable}
              onCommit={v => onCommit?.({ subheadline: v || undefined })}
              placeholder="Subheadline…"
            />
          </p>
        )}
        {props.ctaText && (
          <a
            href={props.ctaUrl ?? '#'}
            className="mt-6 inline-block rounded-lg bg-white px-6 py-3 font-semibold text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {props.ctaText}
          </a>
        )}
      </div>
    </div>
  )
}

function TextPreview({ props, editable, onCommit }: { props: TextProps } & EditableProps) {
  return (
    <EditableText
      tag="div"
      className="prose prose-gray max-w-none px-8 py-8"
      style={{ textAlign: props.alignment }}
      value={props.content}
      html
      editable={!!editable}
      onCommit={v => onCommit?.({ content: v })}
    />
  )
}

function ImagePreview({ props }: { props: ImageProps }) {
  return (
    <div className="px-8 py-6">
      {props.src ? (
        <img
          src={props.src}
          alt={props.alt}
          className="mx-auto rounded-lg w-full object-cover"
          style={{ aspectRatio: props.aspectRatio ?? 'auto', objectFit: props.objectFit }}
        />
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-lg bg-gray-100">
          <ImageIcon className="h-8 w-8 text-gray-300" />
        </div>
      )}
      {props.caption && <p className="mt-2 text-center text-sm text-gray-500">{props.caption}</p>}
    </div>
  )
}

function CtaPreview({ props, editable, onCommit }: { props: CtaProps } & EditableProps) {
  return (
    <div className={`bg-violet-600 px-8 py-16 text-white text-${props.alignment}`}>
      <h2 className="text-3xl font-bold">
        <EditableText
          value={props.headline}
          editable={!!editable}
          onCommit={v => onCommit?.({ headline: v })}
          placeholder="Headline…"
        />
      </h2>
      {(props.description || editable) && (
        <p className="mt-3 text-lg opacity-90">
          <EditableText
            value={props.description ?? ''}
            editable={!!editable}
            onCommit={v => onCommit?.({ description: v || undefined })}
            placeholder="Description…"
          />
        </p>
      )}
      <div className={`mt-6 flex gap-3 ${props.alignment === 'center' ? 'justify-center' : props.alignment === 'right' ? 'justify-end' : 'justify-start'}`}>
        <a href={props.primaryUrl} className="rounded-lg bg-white px-5 py-2.5 font-semibold text-violet-700 hover:bg-gray-100 transition-colors">
          {props.primaryText}
        </a>
        {props.secondaryText && (
          <a href={props.secondaryUrl ?? '#'} className="rounded-lg border border-white/50 px-5 py-2.5 font-semibold text-white hover:bg-white/10 transition-colors">
            {props.secondaryText}
          </a>
        )}
      </div>
    </div>
  )
}

function FeaturesPreview({ props }: { props: FeaturesProps }) {
  const colClass = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[props.columns] ?? 'grid-cols-3'
  return (
    <div className="px-8 py-12">
      {props.headline && <h2 className="mb-2 text-center text-3xl font-bold text-gray-900">{props.headline}</h2>}
      {props.subheadline && <p className="mb-10 text-center text-gray-500">{props.subheadline}</p>}
      <div className={`grid ${colClass} gap-6`}>
        {props.items.length === 0
          ? Array.from({ length: props.columns }).map((_, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="mb-3 h-8 w-8 rounded-lg bg-violet-100" />
                <div className="h-4 w-24 rounded bg-gray-200 mb-2" />
                <div className="h-3 w-full rounded bg-gray-100 mb-1" />
                <div className="h-3 w-3/4 rounded bg-gray-100" />
              </div>
            ))
          : props.items.map((item, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-6">
                <h3 className="mb-2 font-semibold text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.description}</p>
              </div>
            ))}
      </div>
    </div>
  )
}

function SpacerPreview({ props }: { props: SpacerProps }) {
  return <div style={{ height: props.height }} className="bg-transparent" />
}

function DividerPreview() {
  return <div className="px-8 py-4"><hr className="border-gray-200" /></div>
}

function QuotePreview({ props, editable, onCommit }: { props: QuoteProps } & EditableProps) {
  const sizes = { sm: 'text-xl', md: 'text-2xl', lg: 'text-3xl' }
  return (
    <div className="px-8 py-12 text-center">
      <div className="mx-auto max-w-3xl">
        <span style={{ color: props.accentColor ?? '#c9a96e', fontSize: '4rem', lineHeight: 1 }}>"</span>
        <p className={`font-serif italic leading-relaxed ${sizes[props.size ?? 'md']}`}>
          <EditableText
            value={props.quote}
            editable={!!editable}
            onCommit={v => onCommit?.({ quote: v })}
            placeholder="Pull quote text…"
          />
        </p>
        {(props.attribution || editable) && (
          <p className="mt-4 text-sm font-semibold uppercase tracking-widest opacity-60">
            <EditableText
              value={props.attribution ?? ''}
              editable={!!editable}
              onCommit={v => onCommit?.({ attribution: v || undefined })}
              placeholder="Attribution…"
            />
          </p>
        )}
      </div>
    </div>
  )
}

function CardsPreview({ props }: { props: CardsProps }) {
  const cols = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5' }[props.columns ?? 3] ?? 'grid-cols-3'
  const items = props.items.length > 0 ? props.items.slice(0, props.columns ?? 3) : Array.from({ length: props.columns ?? 3 }, () => null)
  return (
    <div className="px-8 py-10">
      {(props.label || props.headline) && (
        <div className="mb-6">
          {props.label && <p className="mb-1 text-xs font-bold uppercase tracking-widest opacity-50">{props.label}</p>}
          {props.headline && <h2 className="text-2xl font-bold">{props.headline}</h2>}
        </div>
      )}
      <div className={`grid ${cols} gap-4`}>
        {items.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-gray-200">
            <div className="aspect-[3/4] bg-gray-100">
              {item?.image && <img src={item.image} alt={item.title} className="h-full w-full object-cover" />}
            </div>
            <div className="p-3">
              <p className="font-semibold text-sm leading-tight">{item?.title ?? 'Card title'}</p>
              {item?.subtitle && <p className="text-xs opacity-50 mt-0.5">{item.subtitle}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProductCatalogPreview({ props }: { props: ProductCatalogProps }) {
  const cols = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[props.columns ?? 3] ?? 'grid-cols-3'
  const items = props.items.length > 0 ? props.items.slice(0, props.columns ?? 3) : Array.from({ length: props.columns ?? 3 }, () => null)
  return (
    <div className="px-8 py-10">
      {props.headline && <h2 className="mb-6 text-2xl font-bold text-center">{props.headline}</h2>}
      <div className={`grid ${cols} gap-4`}>
        {items.map((item, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-gray-200">
            <div className="relative aspect-[4/3] bg-gray-100">
              {item?.image && <img src={item.image} alt={item.name} className={`h-full w-full object-cover ${item.inStock === false ? 'opacity-50 grayscale' : ''}`} />}
              {item && !item.inStock && (
                <span className="absolute right-2 top-2 rounded-full bg-gray-900/80 px-2 py-0.5 text-[10px] font-semibold text-white">Sold out</span>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold leading-tight">{item?.name ?? 'Product name'}</p>
                <p className="shrink-0 text-xs font-bold text-violet-600">{item?.price ?? '$0'}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function TestimonialsPreview({ props }: { props: TestimonialsProps }) {
  return (
    <div className="px-8 py-10">
      {props.headline && <h2 className="mb-6 text-2xl font-bold text-center">{props.headline}</h2>}
      <div className="grid grid-cols-2 gap-4">
        {(props.items.length > 0 ? props.items.slice(0, 2) : [null, null]).map((item, i) => (
          <div key={i} className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm italic text-gray-600">"{item?.quote ?? 'Testimonial text…'}"</p>
            <p className="mt-3 text-xs font-semibold text-gray-900">{item?.author ?? 'Author Name'}</p>
            {item?.role && <p className="text-xs text-gray-400">{item.role}</p>}
          </div>
        ))}
      </div>
    </div>
  )
}

function PricingPreview({ props }: { props: PricingProps }) {
  return (
    <div className="px-8 py-10">
      {props.headline && <h2 className="mb-6 text-2xl font-bold text-center">{props.headline}</h2>}
      <div className="grid grid-cols-3 gap-4">
        {(props.tiers.length > 0 ? props.tiers.slice(0, 3) : Array.from({ length: 3 }, () => null)).map((tier, i) => (
          <div key={i} className={`rounded-xl border p-5 ${tier?.highlighted ? 'border-violet-500 bg-violet-50' : 'border-gray-200 bg-white'}`}>
            <p className="font-semibold">{tier?.name ?? 'Plan'}</p>
            <p className={`mt-1 text-2xl font-bold ${tier?.highlighted ? 'text-violet-600' : ''}`}>{tier?.price ?? '$0'}</p>
            <p className="mt-3 text-xs font-medium opacity-50">{tier?.ctaText ?? 'Get started'}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function LogosPreview({ props }: { props: LogosProps }) {
  return (
    <div className="px-8 py-8">
      {props.headline && <p className="mb-4 text-center text-xs font-semibold uppercase tracking-widest opacity-40">{props.headline}</p>}
      <div className="flex flex-wrap items-center justify-center gap-8">
        {(props.logos.length > 0 ? props.logos : Array.from({ length: 5 }, (_, i) => ({ name: `Brand ${i + 1}`, src: undefined }))).map((logo, i) => (
          logo.src
            ? <img key={i} src={logo.src} alt={logo.name} className="h-8 object-contain opacity-60 grayscale" />
            : <span key={i} className="text-sm font-bold uppercase tracking-wider opacity-30">{logo.name}</span>
        ))}
      </div>
    </div>
  )
}

function StatsPreview({ props, editable, onCommit }: { props: StatsProps } & EditableProps) {
  return (
    <div className="px-8 py-10">
      {(props.headline || editable) && (
        <h2 className="mb-6 text-2xl font-bold text-center">
          <EditableText
            value={props.headline ?? ''}
            editable={!!editable}
            onCommit={v => onCommit?.({ headline: v || undefined })}
            placeholder="Headline…"
          />
        </h2>
      )}
      <div className="grid grid-cols-3 gap-6 text-center">
        {(props.items.length > 0 ? props.items.slice(0, 3) : [{ value: '10k+', label: 'Users' }, { value: '99%', label: 'Uptime' }, { value: '4.9', label: 'Rating' }]).map((stat, i) => (
          <div key={i}>
            <p className="text-3xl font-bold">{stat.value}</p>
            <p className="text-sm opacity-50">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function GalleryPreview({ props }: { props: GalleryProps }) {
  const cols = { 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' }[props.columns] ?? 'grid-cols-3'
  return (
    <div className="px-8 py-6">
      <div className={`grid ${cols} gap-2`}>
        {(props.images.length > 0 ? props.images.slice(0, props.columns) : Array.from({ length: props.columns }, () => null)).map((img, i) => (
          <div key={i} className="aspect-square overflow-hidden rounded-lg bg-gray-100">
            {img?.src && <img src={img.src} alt={img.alt} className="h-full w-full object-cover" />}
          </div>
        ))}
      </div>
    </div>
  )
}

function VideoPreview({ props }: { props: VideoProps }) {
  return (
    <div className="px-8 py-6">
      <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900">
        {props.poster && <img src={props.poster} alt="" className="h-full w-full object-cover opacity-70" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90">
            <div className="ml-1 border-y-8 border-l-12 border-y-transparent border-l-gray-900" style={{ borderLeftWidth: 16 }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function BlogFeedPreview() {
  const posts = [
    { w: 'w-2/3', tw: 'w-1/3' },
    { w: 'w-1/2', tw: 'w-2/5' },
    { w: 'w-3/5', tw: 'w-2/5' },
  ]
  return (
    <div className="px-8 py-8">
      <div className="mb-4 h-4 w-24 rounded bg-gray-200" />
      <div className="grid grid-cols-3 gap-3">
        {posts.map((p, i) => (
          <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="mb-2 h-16 rounded-md bg-gray-200" />
            <div className={`mb-1.5 h-2.5 rounded ${p.w} bg-gray-300`} />
            <div className={`h-2 rounded ${p.tw} bg-gray-200`} />
          </div>
        ))}
      </div>
    </div>
  )
}

const COLUMN_RESIZE_MIN = 10

function ColumnsPreview({ props, editable, onCommit }: { props: ColumnsProps } & EditableProps) {
  const { widths = [50, 50], items = [] } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [liveWidths, setLiveWidths] = useState<number[] | null>(null)
  const dragRef = useRef<{ index: number; startX: number; startWidths: number[]; current: number[] } | null>(null)
  const displayWidths = liveWidths ?? widths

  const resizeByKey = (index: number, deltaPercent: number) => {
    const left = index
    const right = index + 1
    const clampedDelta = Math.min(
      Math.max(deltaPercent, COLUMN_RESIZE_MIN - widths[left]!),
      widths[right]! - COLUMN_RESIZE_MIN,
    )
    const next = [...widths]
    next[left] = widths[left]! + clampedDelta
    next[right] = widths[right]! - clampedDelta
    onCommit?.({ widths: next, items })
  }

  const startDrag = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { index, startX: e.clientX, startWidths: widths, current: widths }
    setLiveWidths(widths)

    const onMouseMove = (ev: MouseEvent) => {
      const drag = dragRef.current
      const containerWidth = containerRef.current?.getBoundingClientRect().width
      if (!drag || !containerWidth) return
      const deltaPercent = ((ev.clientX - drag.startX) / containerWidth) * 100
      const left = drag.index
      const right = drag.index + 1
      const clampedDelta = Math.min(
        Math.max(deltaPercent, COLUMN_RESIZE_MIN - drag.startWidths[left]!),
        drag.startWidths[right]! - COLUMN_RESIZE_MIN,
      )
      const next = [...drag.startWidths]
      next[left] = drag.startWidths[left]! + clampedDelta
      next[right] = drag.startWidths[right]! - clampedDelta
      drag.current = next
      setLiveWidths(next)
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      const drag = dragRef.current
      dragRef.current = null
      setLiveWidths(null)
      if (drag) onCommit?.({ widths: drag.current, items })
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  return (
    <div ref={containerRef} className="flex gap-2 px-6 py-8">
      {displayWidths.map((w, i) => (
        <div key={i} className="relative min-h-16 rounded-lg bg-gray-100 border border-dashed border-gray-200 flex items-center justify-center overflow-hidden"
          style={{ flex: `0 0 ${w}%` }}>
          {items[i]?.content
            ? <div className="prose prose-xs max-w-none px-2 py-1 text-[10px]" dangerouslySetInnerHTML={{ __html: sanitizeRichText(items[i]!.content) }} />
            : <span className="text-[10px] text-gray-300">Column {i + 1}</span>
          }
          {editable && i < displayWidths.length - 1 && (
            /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- a focusable `role="separator"` with aria-valuenow is a valid ARIA APG "movable separator" widget; eslint-plugin-jsx-a11y's static role list doesn't account for that conditional promotion */
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label={`Resize column ${i + 1}`}
              aria-valuenow={Math.round(w)}
              aria-valuemin={COLUMN_RESIZE_MIN}
              aria-valuemax={100 - COLUMN_RESIZE_MIN}
              tabIndex={0}
              onMouseDown={startDrag(i)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') { e.preventDefault(); resizeByKey(i, -2) }
                if (e.key === 'ArrowRight') { e.preventDefault(); resizeByKey(i, 2) }
              }}
              className="group/handle absolute -right-1.5 top-0 bottom-0 z-10 flex w-3 cursor-col-resize items-center justify-center focus:outline-none focus-visible:bg-violet-100"
              title="Drag to resize"
            >
            {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
              <div className="h-8 w-0.5 rounded-full bg-gray-300 transition-colors group-hover/handle:bg-violet-500" />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function GridPreview({ props }: { props: GridProps }) {
  const { columns = 3, items = [] } = props
  const colClass = ['grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][columns - 1] ?? 'grid-cols-3'
  return (
    <div className={`grid ${colClass} gap-2 px-6 py-8`}>
      {Array.from({ length: Math.max(items.length, columns) }).map((_, i) => (
        <div key={i} className="min-h-14 rounded-lg bg-gray-100 border border-dashed border-gray-200 flex items-center justify-center overflow-hidden">
          {items[i]?.content
            ? <div className="prose prose-xs max-w-none px-2 py-1 text-[10px]" dangerouslySetInnerHTML={{ __html: sanitizeRichText(items[i]!.content) }} />
            : <span className="text-[10px] text-gray-300">Cell {i + 1}</span>
          }
        </div>
      ))}
    </div>
  )
}

function ContactFormPreview({ props }: { props: ContactFormProps }) {
  const fields = props.fields?.length
    ? props.fields
    : [{ name: 'name', label: 'Name', type: 'text' as const }, { name: 'email', label: 'Email', type: 'email' as const }, { name: 'message', label: 'Message', type: 'textarea' as const }]
  return (
    <div className="px-8 py-10">
      {props.headline && <h2 className="mb-1 text-2xl font-bold text-gray-900">{props.headline}</h2>}
      {props.subheadline && <p className="mb-6 text-sm text-gray-500">{props.subheadline}</p>}
      <div className="space-y-3">
        {fields.slice(0, 4).map((f, i) => (
          <div key={i}>
            <p className="mb-1 text-xs font-medium text-gray-600">{f.label}{f.required && <span className="text-red-400">*</span>}</p>
            {f.type === 'textarea'
              ? <div className="h-16 w-full rounded-lg border border-gray-200 bg-gray-50" />
              : <div className="h-8 w-full rounded-lg border border-gray-200 bg-gray-50" />
            }
          </div>
        ))}
        <div className="pt-1">
          <div className="inline-block rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white">
            {props.submitText ?? 'Send message'}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmbedPreview({ props }: { props: EmbedProps }) {
  const aspectMap = { '1/1': 'aspect-square', '4/3': 'aspect-[4/3]', '16/9': 'aspect-video' }
  const aspectClass = aspectMap[props.aspectRatio ?? '16/9'] ?? 'aspect-video'
  return (
    <div className="px-8 py-6">
      <div className={`${aspectClass} w-full flex items-center justify-center rounded-lg bg-gray-900 border border-gray-700`}>
        <div className="text-center">
          <Globe className="mx-auto mb-2 h-8 w-8 text-gray-500" />
          <p className="text-xs text-gray-500">Embedded content</p>
        </div>
      </div>
    </div>
  )
}

function HtmlPreview({ props }: { props: HtmlProps }) {
  const preview = (props.code ?? '').slice(0, 120).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  return (
    <div className="px-8 py-6">
      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <Code2 className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 mb-1">Custom HTML</p>
          {preview && <p className="truncate text-xs text-gray-400">{preview}</p>}
        </div>
      </div>
    </div>
  )
}

function PlaceholderPreview({ type }: { type: string }) {
  return (
    <div className="flex min-h-16 items-center justify-center rounded-lg bg-gray-50 border border-dashed border-gray-200">
      <span className="text-xs font-medium capitalize text-gray-400">{type}</span>
    </div>
  )
}

export function BlockPreview({ block, editable = false }: BlockPreviewProps) {
  const { type, props, settings } = block
  const updateBlockProps = useEditorStore(s => s.updateBlockProps)
  const commit = (patch: Partial<BlockProps>) => updateBlockProps(block.id, patch)

  const wrapperStyle: React.CSSProperties = {
    backgroundColor: settings.backgroundColor,
    backgroundImage: settings.backgroundImage ? `url(${settings.backgroundImage})` : undefined,
    backgroundSize: settings.backgroundImage ? 'cover' : undefined,
    paddingTop: settings.paddingTop,
    paddingBottom: settings.paddingBottom,
    paddingLeft: settings.paddingLeft,
    paddingRight: settings.paddingRight,
    color: settings.textColor,
  }

  const p = props as unknown

  const content = (() => {
    switch (type) {
      case 'hero':         return <HeroPreview props={p as HeroProps} editable={editable} onCommit={commit} />
      case 'text':         return <TextPreview props={p as TextProps} editable={editable} onCommit={commit} />
      case 'image':        return <ImagePreview props={p as ImageProps} />
      case 'cta':          return <CtaPreview props={p as CtaProps} editable={editable} onCommit={commit} />
      case 'features':     return <FeaturesPreview props={p as FeaturesProps} />
      case 'spacer':       return <SpacerPreview props={p as SpacerProps} />
      case 'divider':      return <DividerPreview />
      case 'quote':        return <QuotePreview props={p as QuoteProps} editable={editable} onCommit={commit} />
      case 'cards':        return <CardsPreview props={p as CardsProps} />
      case 'product-catalog': return <ProductCatalogPreview props={p as ProductCatalogProps} />
      case 'testimonials': return <TestimonialsPreview props={p as TestimonialsProps} />
      case 'pricing':      return <PricingPreview props={p as PricingProps} />
      case 'logos':        return <LogosPreview props={p as LogosProps} />
      case 'stats':        return <StatsPreview props={p as StatsProps} editable={editable} onCommit={commit} />
      case 'gallery':      return <GalleryPreview props={p as GalleryProps} />
      case 'video':        return <VideoPreview props={p as VideoProps} />
      case 'blog-feed':    return <BlogFeedPreview />
      case 'columns':       return <ColumnsPreview props={p as ColumnsProps} editable={editable} onCommit={commit} />
      case 'grid':          return <GridPreview props={p as GridProps} />
      case 'contact-form':  return <ContactFormPreview props={p as ContactFormProps} />
      case 'embed':         return <EmbedPreview props={p as EmbedProps} />
      case 'html':          return <HtmlPreview props={p as HtmlProps} />
      default:              return <PlaceholderPreview type={type} />
    }
  })()

  return <div style={wrapperStyle}>{content}</div>
}
